import { ClassFlag } from "../../library-js/dom-tools/ClassFlag";
import { ElementType } from "../../library-js/dom-tools/ElementType";
import { ElementContainer } from "../../library-js/dom-tools/ElementContainer";
import { TemplateDefaults } from "../TemplateDefaults";
import { ContextMenuEntry } from "./ContextMenuEntry";
import { ContextMenuItem } from "./ContextMenuItem";
import { ContextMenuSeperator } from "./ContextMenuSeperator";
import { ContextMenuSubMenu } from "./ContextMenuSubMenu";
import { Vector2 } from "three";
import { nextFrame } from "../../library-js/animation/nextFrame";
import { HTMLEditor } from "../../library-js/dom-tools/HTMLEditor";
import { DOMHitTest } from "../../library-js/dom-tools/DOMHitTest";
import { InputManager } from "../../library-js/dom-tools/InputManager";
import { OnClick, OnMouseDown } from "../../library-js/dom-tools/EventListeners";
import { MathX } from "../../library-js/math/MathX";

export class ContextMenu extends ElementContainer<HTMLElement>
{  
  static readonly type   = new ElementType<HTMLElement>( "context--menu" );
  static readonly header = new ElementType( "context--menu--header" );

  private static debugDurationMS = 0;


  protected _parent:ContextMenu;
  protected _children:ContextMenuItem[] = [];
  protected _header:Element;
  
  protected _hideTime:number = 0;
  protected _removeTime:number = 0;
  protected _initialHideDurationMS = 1000 + ContextMenu.debugDurationMS;
  protected _hoveredShowDurationMS = 200  + ContextMenu.debugDurationMS;
  protected _canUnhide = true;
  protected _canHide = false;

  get active(){ return ClassFlag.active.in( this.element );}

  cloneHeader():Element
  {
    return this._header.cloneNode( true ) as Element;
  }
  
  constructor( header:string|Element )
  {
    super();
    this._header = typeof header === "string" ? ElementType.span.create( header ) : header;
    this._create();
  }

  update( inputManager:InputManager )
  {
    if ( ! this._canHide )
    {
      return;
    }

    let now = new Date().getTime();

    let isHovered = inputManager.isHovered( this.element );

    if ( isHovered )
    {
      this.triggerVisible();
      return;
    }


    if ( now > this._hideTime )
    {
      if ( ClassFlag.visible.in( this.element ) )
      {
        ClassFlag.visible.removeFrom( this.element );
      }      

      let computedOpacity = parseFloat( window.getComputedStyle( this.element ).opacity );

      if ( computedOpacity < 0.01 && ! ClassFlag.visible.in( this.element ) )
      {
        ClassFlag.active.removeFrom( this.element );
        ClassFlag.enabled.removeFrom( this.element );
        HTMLEditor.remove( this._element );
        
      }
    }

  }

  close()
  {
    this._deactivate();
  }
  
  _deactivate()
  {
    ClassFlag.visible.removeFrom( this.element );
    ClassFlag.enabled.removeFrom( this.element );
    this._hideTime = new Date().getTime();
  }


  triggerVisible()
  {
    if ( ! this.active )
    {
      return;
    }

    if ( ! ClassFlag.enabled.in( this.element ) )
    {
      return;
    }

    
    let now = new Date().getTime();

    ClassFlag.visible.set( this.element );
    this._hideTime = now + this._hoveredShowDurationMS;
  }

  attachTo( parentElement:Element, anchorsUV?:Vector2, boundsUV?:Vector2, offsetPixels?:Vector2 )
  {
    anchorsUV    = anchorsUV || new Vector2( 0, 0 );
    boundsUV     = boundsUV || new Vector2( 0, 1 );
    offsetPixels = offsetPixels || new Vector2( 0, 5 );

    let box = DOMHitTest.getPageBox( parentElement );

    

    let position = new Vector2( 
      MathX.lerp( box.min.x, box.max.x, boundsUV.x ),
      MathX.lerp( box.min.y, box.max.y, boundsUV.y ),
    )

    let ownBox = DOMHitTest.getPageBox( this.element );
    let anchorOffset = ownBox.getSize( new Vector2() );

    anchorOffset.multiply( anchorsUV );

    position.sub( anchorOffset );

    position.add( offsetPixels )
    this.setElementPosition( position );

    let clientRect = parentElement.getBoundingClientRect();
    console.log( { box, parentElement, clientRect, anchorsUV, boundsUV, offsetPixels, ownBox, anchorOffset } );
  }

  dontHide()
  {
    this._canHide = false;
  }

  canHide()
  {
    this._canHide = true;
    this._hideTime = new Date().getTime() + this._hoveredShowDurationMS;
  }

  async show( parentElement:Element, attachElement?:Element )
  {
    ClassFlag.active.set( this._element );

    parentElement.appendChild( this.element );


    this.attachTo( attachElement || parentElement );

    this._hideTime = new Date().getTime() + this._initialHideDurationMS;

    await nextFrame();


    let removeSelf = () => 
    { 
      this._deactivate();       
      OnClick.remove( document.body, removeSelf );
    }

    OnClick.add( document.body, removeSelf );

    ClassFlag.visible.set( this._element );
    ClassFlag.enabled.set( this._element );
    ClassFlag.smooth.set( this._element );
    
  }

  protected _create()
  {
    this._createElement( ContextMenu.type );        
  }

  protected _addToMenu( item:ContextMenuItem )
  {
    if ( this._children.length > 0 )
    {
      this.appendBreak();
    }

    this._children.push( item );
    this.appendChild( item );  
    
    return item;
  }

  addMenu( contextMenu:ContextMenu )
  {
    this._addToMenu( new ContextMenuSubMenu( this, contextMenu ) );
  }

  addEntry( entry:string|Element )
  {
    return this._addToMenu( new ContextMenuEntry( this, entry ) );
  }

  addSeperator()
  {
    this._addToMenu( new ContextMenuSeperator( this ) );
  }
}